import { parse as parseQueryParams } from 'querystring';

import { type MarketingPayload } from '~/types/RegisterUser';

/**
 * The platform (or vendor) where the traffic originates, like Facebook
 * or your email newsletter.
 */
type UTMSource = string;

/**
 * You can use this to identify the medium like Cost Per Click (CPC),
 * social media, affiliate or QR code.
 */
type UTMMedium = string;

/**
 * This is just to identify your campaign. Like your website or a
 * specific product promotion.
 */
type UTMCampaign = string;

/**
 * You’ll use this mainly for tracking your keywords during a paid AdWords
 * campaign. You can also use it in your display ad campaigns to identify
 * aspects of your audience.
 */
type UTMTerm = string;

/**
 * If you’re A/B testing ads, then this is a useful metric that passes
 * details about your ad. You can also use it to differentiate links
 * that point to the same URL.
 */
type UTMContent = string;

/**
 * UTM parameters as described by Google analytics. Terms and content are the
 * only optional parameters.
 *
 * See: https://neilpatel.com/blog/the-ultimate-guide-to-using-utm-parameters/
 * See: https://support.google.com/analytics/answer/1033863#parameters
 */
export interface RawUTMParams {
  utm_campaign: UTMCampaign;
  utm_content?: UTMContent;
  utm_medium: UTMMedium;
  utm_source: UTMSource;
  utm_term?: UTMTerm;
}

/**
 * Normalized version of UTM parameters as described by Google analytics. Terms
 * and content are the only optional parameters.
 *
 * See: https://neilpatel.com/blog/the-ultimate-guide-to-using-utm-parameters/
 * See: https://support.google.com/analytics/answer/1033863#parameters
 */
export type UTMParams = {
  campaign: UTMCampaign;
  content: UTMContent;
  medium: UTMMedium;
  source: UTMSource;
  term: UTMTerm;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeUTMParameters = (rawParams: any): UTMParams => {
  const { utm_campaign, utm_content, utm_medium, utm_source, utm_term }: RawUTMParams = rawParams;
  return {
    campaign: decodeURIComponent(utm_campaign || ''),
    content: decodeURIComponent(utm_content || ''),
    medium: decodeURIComponent(utm_medium || ''),
    source: decodeURIComponent(utm_source || ''),
    term: decodeURIComponent(utm_term || ''),
  };
};

/**
 * Takes a url and extracts out the query string or "search" string.
 *
 * @function extractQueryStringFromURL
 * @param {string} url
 * @private
 */
const extractQueryStringFromURL = (url: string): string => {
  let querystring = '';

  if (window && window.URL) {
    const urlObj = new window.URL(url);
    querystring = urlObj.search.replace(/^\?/, '');
  } else if (window && window.document) {
    const anchor = window.document.createElement('a');
    querystring = anchor.search.replace(/^\?/, '');
  } else {
    // NOTE: this is not a surefire way to ensure queryparams
    // but should only be triggered in rare cases.
    querystring = url.replace(/.*?\?/, '');
  }

  return querystring;
};

/**
 * Extracts the UTM parameters from the url
 *
 * @function extractUTMParameters
 * @param {string} url
 * @public
 */
export const extractUTMParameters = (url: string) => {
  const querystring = extractQueryStringFromURL(url);
  const queryParams = parseQueryParams(querystring);
  return normalizeUTMParameters(queryParams);
};

const SHOPIFY_REGEX = /shopify/gi;

/**
 * Returns a boolean for if the UTM params signal a referral from Shopify
 *
 * @function isReferredByShopify
 * @param {object} queryParams - an object that has the standard UTM query params
 * @returns {boolean} Boolean
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isReferredByShopify = (queryParams: any): boolean => {
  const utm: UTMParams = normalizeUTMParameters(queryParams);
  return [utm.source, utm.content].some((utmParam) => utmParam.search(SHOPIFY_REGEX) !== -1);
};

export const extractMarketingPayloadFromCookie = (cookie): MarketingPayload => ({
  attributionSnippet: cookie.attribution_snippet || '',
  campaign: cookie.utm_campaign || '',
  campaignContent: cookie.utm_content || '',
  campaignTerm: cookie.utm_term || '',
  httpReferrer: cookie.http_referrer || '',
  medium: cookie.utm_medium || '',
  rawHttpReferrer: document.referrer,
  rawQueryString: document.location ? document.location.search : undefined,
  shAd1: cookie.sh_ad1 || '',
  shAd2: cookie.sh_ad2 || '',
  shAd3: cookie.sh_ad3 || '',
  shAd4: cookie.sh_ad4 || '',
  shDevice: cookie.sh_device || '',
  shMatchtype: cookie.sh_matchtype || '',
  source: cookie.utm_source || '',
});

export default {
  extractMarketingPayloadFromCookie,
  extractParameters: extractUTMParameters,
  isReferredByShopify,
  normalizeParameters: normalizeUTMParameters,
};
